/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import { addMetaToQuery } from '../../mutators/add-meta';
import type {
  CreateEventRequest,
  CreateSiteActivityEventParams,
  Error,
  EventResponse,
  ListEventsResponse,
  ListSiteActivityEventsParams,
  SiteID,
  SuccessResponse,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Stops recording WordPress admin activity on this site. Existing log entries are kept.
 * @summary Turn off activity logging
 */
export const disableSiteActivity = (id: SiteID, signal?: AbortSignal) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/${id}/activity/disable`,
    method: 'POST',
    signal,
  });
};

export const getDisableSiteActivityMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof disableSiteActivity>>,
    TError,
    { id: SiteID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof disableSiteActivity>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationKey = ['disableSiteActivity'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof disableSiteActivity>>,
    { id: SiteID }
  > = props => {
    const { id } = props ?? {};

    return disableSiteActivity(id);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/activity/disable` },
    {
      operationId: 'app.controllers.activity_controller.disable_site_activity',
      operationName: 'disableSiteActivity',
    }
  );

  return customOptions;
};

export type DisableSiteActivityMutationResult = NonNullable<
  Awaited<ReturnType<typeof disableSiteActivity>>
>;

export type DisableSiteActivityMutationError = Error;

/**
 * @summary Turn off activity logging
 */
export const useDisableSiteActivity = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof disableSiteActivity>>,
      TError,
      { id: SiteID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof disableSiteActivity>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationOptions = getDisableSiteActivityMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Starts recording WordPress admin actions such as logins, content changes, and plugin updates on this site.
 * @summary Turn on activity logging
 */
export const enableSiteActivity = (id: SiteID, signal?: AbortSignal) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/${id}/activity/enable`,
    method: 'POST',
    signal,
  });
};

export const getEnableSiteActivityMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof enableSiteActivity>>,
    TError,
    { id: SiteID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof enableSiteActivity>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationKey = ['enableSiteActivity'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof enableSiteActivity>>,
    { id: SiteID }
  > = props => {
    const { id } = props ?? {};

    return enableSiteActivity(id);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/activity/enable` },
    {
      operationId: 'app.controllers.activity_controller.enable_site_activity',
      operationName: 'enableSiteActivity',
    }
  );

  return customOptions;
};

export type EnableSiteActivityMutationResult = NonNullable<
  Awaited<ReturnType<typeof enableSiteActivity>>
>;

export type EnableSiteActivityMutationError = Error;

/**
 * @summary Turn on activity logging
 */
export const useEnableSiteActivity = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof enableSiteActivity>>,
      TError,
      { id: SiteID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof enableSiteActivity>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationOptions = getEnableSiteActivityMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Manually adds an activity log entry with author, action, description, IP, and related details.
 * @summary Log a custom activity event
 */
export const createSiteActivityEvent = (
  id: SiteID,
  createEventRequest: CreateEventRequest,
  params?: CreateSiteActivityEventParams,
  signal?: AbortSignal
) => {
  return customFetch<EventResponse>({
    url: `/v1/sites/${id}/activity/events`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createEventRequest,
    params,
    signal,
  });
};

export const getCreateSiteActivityEventMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSiteActivityEvent>>,
    TError,
    { id: SiteID; data: CreateEventRequest; params?: CreateSiteActivityEventParams },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSiteActivityEvent>>,
  TError,
  { id: SiteID; data: CreateEventRequest; params?: CreateSiteActivityEventParams },
  TContext
> => {
  const mutationKey = ['createSiteActivityEvent'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSiteActivityEvent>>,
    { id: SiteID; data: CreateEventRequest; params?: CreateSiteActivityEventParams }
  > = props => {
    const { id, data, params } = props ?? {};

    return createSiteActivityEvent(id, data, params);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/activity/events` },
    {
      operationId: 'app.controllers.activity_controller.create_site_activity_event',
      operationName: 'createSiteActivityEvent',
    }
  );

  return customOptions;
};

export type CreateSiteActivityEventMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSiteActivityEvent>>
>;
export type CreateSiteActivityEventMutationBody = CreateEventRequest;
export type CreateSiteActivityEventMutationError = Error;

/**
 * @summary Log a custom activity event
 */
export const useCreateSiteActivityEvent = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSiteActivityEvent>>,
      TError,
      { id: SiteID; data: CreateEventRequest; params?: CreateSiteActivityEventParams },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSiteActivityEvent>>,
  TError,
  { id: SiteID; data: CreateEventRequest; params?: CreateSiteActivityEventParams },
  TContext
> => {
  const mutationOptions = getCreateSiteActivityEventMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Search and filter WordPress admin activity on a site by user, date, action, IP, and more.
 * @summary List site activity
 */
export const listSiteActivityEvents = (
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  signal?: AbortSignal
) => {
  return customFetch<ListEventsResponse>({
    url: `/v1/sites/${id}/activity/events`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListSiteActivityEventsQueryKey = (
  id?: SiteID,
  params?: ListSiteActivityEventsParams
) => {
  return [`/v1/sites/${id}/activity/events`, ...(params ? [params] : [])] as const;
};

export const getListSiteActivityEventsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteActivityEventsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteActivityEvents>>> = ({ signal }) =>
    listSiteActivityEvents(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/activity/events`,
      operationId: 'app.controllers.activity_controller.list_site_activity_events',
      operationName: 'listSiteActivityEvents',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listSiteActivityEvents>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteActivityEventsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteActivityEvents>>
>;
export type ListSiteActivityEventsQueryError = Error;

export function useListSiteActivityEvents<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | ListSiteActivityEventsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteActivityEvents>>,
          TError,
          Awaited<ReturnType<typeof listSiteActivityEvents>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteActivityEvents<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteActivityEvents>>,
          TError,
          Awaited<ReturnType<typeof listSiteActivityEvents>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteActivityEvents<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List site activity
 */

export function useListSiteActivityEvents<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteActivityEventsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListSiteActivityEventsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteActivityEventsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteActivityEvents>>> = ({ signal }) =>
    listSiteActivityEvents(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/activity/events`,
      operationId: 'app.controllers.activity_controller.list_site_activity_events',
      operationName: 'listSiteActivityEvents',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listSiteActivityEvents>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteActivityEventsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteActivityEvents>>
>;
export type ListSiteActivityEventsSuspenseQueryError = Error;

export function useListSiteActivityEventsSuspense<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | ListSiteActivityEventsParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteActivityEventsSuspense<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteActivityEventsSuspense<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List site activity
 */

export function useListSiteActivityEventsSuspense<
  TData = Awaited<ReturnType<typeof listSiteActivityEvents>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteActivityEventsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteActivityEvents>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteActivityEventsSuspenseQueryOptions(id, params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns full details for one activity log entry.
 * @summary Get an activity event
 */
export const findSiteActivityEvent = (id: SiteID, eventId: string, signal?: AbortSignal) => {
  return customFetch<EventResponse>({
    url: `/v1/sites/${id}/activity/events/${eventId}`,
    method: 'GET',
    signal,
  });
};

export const getFindSiteActivityEventQueryKey = (id?: SiteID, eventId?: string) => {
  return [`/v1/sites/${id}/activity/events/${eventId}`] as const;
};

export const getFindSiteActivityEventQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteActivityEventQueryKey(id, eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteActivityEvent>>> = ({ signal }) =>
    findSiteActivityEvent(id, eventId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, eventId },
    {
      url: `/v1/sites/${id}/activity/events/${eventId}`,
      operationId: 'app.controllers.activity_controller.find_site_activity_event',
      operationName: 'findSiteActivityEvent',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findSiteActivityEvent>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteActivityEventQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteActivityEvent>>
>;
export type FindSiteActivityEventQueryError = Error;

export function useFindSiteActivityEvent<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteActivityEvent>>,
          TError,
          Awaited<ReturnType<typeof findSiteActivityEvent>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteActivityEvent<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteActivityEvent>>,
          TError,
          Awaited<ReturnType<typeof findSiteActivityEvent>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteActivityEvent<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get an activity event
 */

export function useFindSiteActivityEvent<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteActivityEventQueryOptions(id, eventId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindSiteActivityEventSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteActivityEventQueryKey(id, eventId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteActivityEvent>>> = ({ signal }) =>
    findSiteActivityEvent(id, eventId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, eventId },
    {
      url: `/v1/sites/${id}/activity/events/${eventId}`,
      operationId: 'app.controllers.activity_controller.find_site_activity_event',
      operationName: 'findSiteActivityEvent',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findSiteActivityEvent>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteActivityEventSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteActivityEvent>>
>;
export type FindSiteActivityEventSuspenseQueryError = Error;

export function useFindSiteActivityEventSuspense<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteActivityEventSuspense<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteActivityEventSuspense<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get an activity event
 */

export function useFindSiteActivityEventSuspense<
  TData = Awaited<ReturnType<typeof findSiteActivityEvent>>,
  TError = Error,
>(
  id: SiteID,
  eventId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteActivityEvent>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteActivityEventSuspenseQueryOptions(id, eventId, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Permanently removes one activity log entry from the site.
 * @summary Delete an activity event
 */
export const deleteSiteActivityEvent = (id: SiteID, eventId: string) => {
  return customFetch<EventResponse>({
    url: `/v1/sites/${id}/activity/events/${eventId}`,
    method: 'DELETE',
  });
};

export const getDeleteSiteActivityEventMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSiteActivityEvent>>,
    TError,
    { id: SiteID; eventId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSiteActivityEvent>>,
  TError,
  { id: SiteID; eventId: string },
  TContext
> => {
  const mutationKey = ['deleteSiteActivityEvent'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSiteActivityEvent>>,
    { id: SiteID; eventId: string }
  > = props => {
    const { id, eventId } = props ?? {};

    return deleteSiteActivityEvent(id, eventId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/activity/events/{eventId}` },
    {
      operationId: 'app.controllers.activity_controller.delete_site_activity_event',
      operationName: 'deleteSiteActivityEvent',
    }
  );

  return customOptions;
};

export type DeleteSiteActivityEventMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSiteActivityEvent>>
>;

export type DeleteSiteActivityEventMutationError = Error;

/**
 * @summary Delete an activity event
 */
export const useDeleteSiteActivityEvent = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteSiteActivityEvent>>,
      TError,
      { id: SiteID; eventId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteSiteActivityEvent>>,
  TError,
  { id: SiteID; eventId: string },
  TContext
> => {
  const mutationOptions = getDeleteSiteActivityEventMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
